import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';

const HeaderContainer = styled.header`
  position: sticky;
  top: 0;
  background-color: var(--background-light);
  box-shadow: 0 3px 20px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: all 0.3s ease;
  padding: ${({ scrolled }) => (scrolled ? '0.5rem 0' : '1rem 0')};
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, var(--primary-color) 0%, var(--accent-color) 100%);
  }
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: var(--container-width);
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--primary-color);
  font-family: 'Poppins', sans-serif;
  
  img, svg {
    height: 50px;
    width: auto;
    transition: all 0.3s ease;
    filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.1));
  }
  
  &:hover img {
    filter: drop-shadow(0px 4px 12px rgba(0, 0, 0, 0.2));
    transform: scale(1.03);
  }
  
  @media (max-width: 768px) {
    img, svg {
      height: 40px;
    }
  }
`;

const NavMenu = styled.nav`
  display: flex;
  align-items: center;
  
  @media (max-width: 992px) {
    position: fixed;
    flex-direction: column;
    background-color: var(--background-light);
    top: 0;
    right: ${({ isOpen }) => (isOpen ? '0' : '-100%')};
    width: 300px;
    height: 100vh;
    padding: 5rem 2rem 2rem;
    transition: right 0.3s ease-in-out;
    box-shadow: -2px 0 15px rgba(0, 0, 0, 0.1);
    z-index: 100;
    overflow-y: auto;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  
  @media (max-width: 992px) {
    flex-direction: column;
    width: 100%;
  }
`;

const NavItem = styled.li`
  margin: 0 0.75rem;
  position: relative;
  
  @media (max-width: 992px) {
    margin: 0.5rem 0;
    width: 100%;
  }
`;

const NavLinkStyled = styled(NavLink)`
  color: var(--text-dark);
  font-weight: 500;
  position: relative;
  padding: 0.75rem 0.5rem;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  letter-spacing: 0.5px;
  
  svg {
    margin-left: 0.25rem;
    font-size: 0.75rem;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0.25rem;
    left: 0.5rem;
    width: 0;
    height: 2px;
    background-color: var(--primary-color);
    transition: width 0.3s ease;
  }
  
  &:hover, &.active {
    color: var(--primary-color);
    
    &:after {
      width: calc(100% - 1rem);
    }
  }
  
  @media (max-width: 992px) {
    display: block;
    padding: 1rem 0;
    font-size: 1.1rem;
    border-bottom: 1px solid var(--border-color);
  }
`;

const CallToAction = styled(Link)`
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius);
  font-weight: 600;
  margin-left: 1.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
  border: none;
  letter-spacing: 0.5px;
  
  &:hover {
    background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-color) 100%);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
  }
  
  @media (max-width: 992px) {
    margin: 1.5rem 0 0;
    width: 100%;
    text-align: center;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--primary-color);
  cursor: pointer;
  z-index: 200;
  
  @media (max-width: 992px) {
    display: block;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  z-index: 50;
  backdrop-filter: blur(3px);
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  min-width: 220px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: all 0.3s ease;
  z-index: 10;
  overflow: hidden;
  
  ${NavItem}:hover & {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
  
  @media (max-width: 992px) {
    position: static;
    opacity: 1;
    visibility: visible;
    transform: none;
    box-shadow: none;
    min-width: auto;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    background-color: var(--background-off-white);
    border-radius: var(--border-radius);
  }
`;

const DropdownList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const DropdownItem = styled.li`
  &:not(:last-child) {
    border-bottom: 1px solid var(--border-color);
  }
`;

const DropdownLink = styled(Link)`
  display: block;
  padding: 0.75rem 1rem;
  color: var(--text-dark);
  font-size: 0.95rem;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--background-off-white);
    color: var(--primary-color);
    padding-left: 1.25rem;
  }
`;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  
  const closeMenu = () => {
    setIsOpen(false);
  };
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 80) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  
  return (
    <HeaderContainer scrolled={scrolled}>
      <NavContainer>
        <Logo to="/">
          <img src="/logo.jpeg" alt="Talgo Logo" />
        </Logo>
        
        <MobileMenuButton onClick={toggleMenu}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </MobileMenuButton>
        
        <Overlay isOpen={isOpen} onClick={closeMenu} />
        
        <NavMenu isOpen={isOpen}>
          <NavList>
            <NavItem>
              <NavLinkStyled to="/" onClick={closeMenu}>
                Home
              </NavLinkStyled>
            </NavItem>
            <NavItem>
              <NavLinkStyled to="/about" onClick={closeMenu}>
                About Us
              </NavLinkStyled>
            </NavItem>
            <NavItem>
              <NavLinkStyled to="/services" onClick={closeMenu}>Services</NavLinkStyled>
            </NavItem>
            <NavItem>
              <NavLinkStyled to="/case-studies" onClick={closeMenu}>
                Case Studies
              </NavLinkStyled>
            </NavItem>
            <NavItem>
              <NavLinkStyled to="/blog" onClick={closeMenu}>
                Blog
              </NavLinkStyled>
            </NavItem>
            <NavItem>
              <NavLinkStyled to="/contact" onClick={closeMenu}>
                Contact
              </NavLinkStyled>
            </NavItem>
          </NavList>
          
          <CallToAction to="/contact">
            Request Demo
          </CallToAction>
        </NavMenu>
      </NavContainer>
    </HeaderContainer>
  );
};

export default Header; 